<script setup lang="ts">
import ProductTeaserContainer from '@/components/ui/ProductTeaserContainer.vue';
import ProductTeaserSkeleton from '@/components/ui/ProductTeaserSkeleton.vue';
import ProductListEmptyState from '@/components/productList/ProductListEmptyState.vue';
import ResultListTeaser from '@/components/productList/ResultListTeaser.vue';
import { computed, watch, onMounted, onUnmounted } from 'vue';
import BasePagination from '@/components/ui/BasePagination.vue';
import FilterBar from '@/components/productList/FilterBar.vue';
import type { SortOptionItem, FilterItem } from '@/types/FilterBar.ts';
import type { RoomOccupancy } from '@/types/SearchBar.ts';
import SearchBar from '@/components/ui/SearchBar.vue';
import BaseHeadline from '@/components/ui/BaseHeadline.vue';
import { ModalType, useModalStore } from '@/stores/modal.ts';
import { usePlayerAvailableValuesStore } from '@/stores/playerAvailableValues.ts';
import { useFilterConfigurationStore } from '@/stores/filterConfiguration.ts';
import { useProductListFiltersStore, SortOption } from '@/stores/productListFilters.ts';
import { useWishlistStore } from '@/stores/wishlist.ts';
import { useResultListTeaserStore } from '@/stores/resultListTeaser.ts';
import { $t } from '@/composables/translations/useTranslate.ts';
import { useFilterInitializer } from '@/composables/filters/useFilterInitializer.ts';
import {
  useBackForwardNavigation,
  isBackForwardNavigation,
} from '@/composables/useBackForwardNavigation.ts';
import { resolveActiveFilterLabels } from '@/utils/filters/filterLabelResolver.ts';
import { useAnalytics } from '@/composables/useAnalytics';
import { SessionStorageService } from '@/services/storage/sessionStorageService.ts';
import DateRangeModal from '@/components/shared/DateRangeModal.vue';
import ProductListModals from '@/components/productList/ProductListModals.vue';

withDefaults(
  defineProps<{
    showSearchBar?: boolean;
    showFilters?: boolean;
  }>(),
  {
    showSearchBar: true,
    showFilters: true,
  }
);

const modalStore = useModalStore();
const availableValuesStore = usePlayerAvailableValuesStore();
const filterConfigurationStore = useFilterConfigurationStore();
const filtersStore = useProductListFiltersStore();
const wishlistStore = useWishlistStore();
const teaserStore = useResultListTeaserStore();
const { initializeFilters, resetFiltersToDefaults } = useFilterInitializer();
const analytics = useAnalytics();

/**
 * Persist the SRP URL so the PDP can navigate back to it. `pagehide` captures
 * the final, fully-synced URL when the user clicks through to a product page.
 */
function captureSrpUrl() {
  SessionStorageService.writeSrpUrl(window.location.href);
}

onMounted(async () => {
  wishlistStore.loadFromCookie();
  await initializeFilters(isBackForwardNavigation());
  teaserStore.fetchTeaser();
  await filtersStore.fetchProductList();
  analytics.trackSrpViewed();
  captureSrpUrl();
  window.addEventListener('pagehide', captureSrpUrl);
});

onUnmounted(() => {
  window.removeEventListener('pagehide', captureSrpUrl);
});

/**
 * Re-hydrate filters when the user returns via browser Back/Forward.
 * Covers BFCache restore (Safari/Firefox) where `onMounted` does not run
 * and standard popstate paths where it does.
 */
useBackForwardNavigation({
  onBackForward: async () => {
    await initializeFilters(true);
    await filtersStore.fetchProductList();
  },
});

const products = computed(() => filtersStore.products);

const TEASER_AFTER_INDEX = 6;
const showTeaser = computed(() => products.value.length > TEASER_AFTER_INDEX);
const productsBeforeTeaser = computed(() => products.value.slice(0, TEASER_AFTER_INDEX));
const productsAfterTeaser = computed(() => products.value.slice(TEASER_AFTER_INDEX));

/**
 * Compute search parameters for product URLs
 *
 * Only includes search params when date range is available (required for offer page).
 * Returns undefined if date range is missing, causing ProductTeaser to use simple slug.
 */
const searchParams = computed(() => {
  if (!filtersStore.dateRange) {
    return undefined;
  }

  return {
    dateRange: filtersStore.dateRange,
    duration: filtersStore.duration,
    // The deep link is the only way the Exact intent reaches the PDP.
    durationMode: filtersStore.durationMode,
    roomOccupancy: filtersStore.roomOccupancy,
  };
});

// Filter
const selectedCountryChips = computed<FilterItem[]>(() => {
  if (filterConfigurationStore.forcedParameterFields.has('dest')) return [];
  return filtersStore.selectedCountries.map((code) => {
    const name = availableValuesStore.countries.find((c) => c.Code === code)?.Name ?? code;
    return {
      id: `country.${code}`,
      label: name,
      category: 'country',
      kind: 'country',
      value: code,
    };
  });
});

const activeFilters = computed<FilterItem[]>(() => [
  ...selectedCountryChips.value,
  ...resolveActiveFilterLabels(
    filtersStore.selectedAccommodationProperties,
    filterConfigurationStore.filterGroups
  ),
]);

const sortingOptions: SortOptionItem[] = [
  {
    value: SortOption.RECOMMENDATION,
    label: $t('ibe.product-list.sort-recommended', 'Von Vamos empfohlen'),
  },
  {
    value: SortOption.PRICE_ASC,
    label: $t('ibe.product-list.sort-price-asc', 'Preis (niedrig zu hoch)'),
  },
];

const selectedSort = computed({
  get: () => filtersStore.sortBy,
  set: (value) => filtersStore.setSortBy(value),
});

const fixedBookableOnly = computed({
  get: () => filtersStore.fixedBookableOnly,
  set: (value) => filtersStore.setFixedBookableOnly(value),
});

const handleOpenFilters = () => {
  modalStore.openModal(ModalType.FILTERS);
};

const handleRemoveFilter = (filter: FilterItem) => {
  if (filter.kind === 'country' && typeof filter.value === 'string') {
    filtersStore.setCountries(filtersStore.selectedCountries.filter((c) => c !== filter.value));
    return;
  }
  if (filter.value && typeof filter.value === 'string') {
    filtersStore.toggleAccommodationProperty(filter.value);
  }
};

const handleResetFilters = () => {
  filtersStore.setAccommodationProperties([]);
  filtersStore.setFixedBookableOnly(false);
  if (!filterConfigurationStore.forcedParameterFields.has('dest')) {
    filtersStore.setCountries([]);
  }
};

const handleResetToDefaults = async () => {
  await resetFiltersToDefaults();
};

const handleSortChange = (sortValue: SortOption) => {
  filtersStore.setSortBy(sortValue);
};

// Search Bar
const handleCountriesChange = (selectedIds: string[]) => {
  filtersStore.setCountries(selectedIds);
};

const handleRoomOccupancyChange = (newRoomOccupancy: RoomOccupancy) => {
  filtersStore.setRoomOccupancy(newRoomOccupancy);
};

const handleSearchClick = async () => {
  await filtersStore.fetchProductList();
};

watch(
  () => filtersStore.apiParams,
  async (newParams) => {
    if (newParams) {
      await filtersStore.fetchProductList();
    } else {
      console.warn('apiParams is null, cannot fetch products');
    }
  },
  { deep: true }
);

// Pagination
const currentPageComputed = computed({
  get: () => filtersStore.currentPage,
  set: (value) => filtersStore.setCurrentPage(value),
});

const totalProducts = computed(() => filtersStore.totalResults);

const loading = computed(() => filtersStore.loading);
const error = computed(() => filtersStore.error);

// Empty state
const hasNoResults = computed(() => !loading.value && products.value.length === 0);

// Auto-scroll to top on pagination change
watch(currentPageComputed, () => {
  const appContainer = document.querySelector('[data-app-embed]');
  if (appContainer) {
    appContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

// Fetch available values when the date modal opens
watch(
  () => modalStore.isModalOpen(ModalType.DATE_PICKER),
  async (isOpen) => {
    if (isOpen) {
      if (availableValuesStore.isStale || !availableValuesStore.hasData) {
        try {
          await availableValuesStore.fetchAvailableValues();
        } catch (error) {
          console.error('Failed to fetch available values:', error);
        }
      }
    }
  }
);

const dateConstraints = computed(() => ({
  min: availableValuesStore.dateRangeForInput.min || undefined,
  max: availableValuesStore.dateRangeForInput.max || undefined,
}));
</script>

<template>
  <BaseHeadline
    v-if="showSearchBar"
    :title="$t('ibe.product-list.search-results', 'Search results')"
    container-margin="lg:mb-12 mb-8"
    heading-level="h1"
    text-align="center"
    heading-class="font-bold"
  />
  <div v-if="showSearchBar" class="lg:mb-14 mb-10">
    <SearchBar
      :countries="availableValuesStore.countries"
      :selected-country-ids="filtersStore.selectedCountries"
      :date-range="filtersStore.dateRange"
      :room-occupancy="filtersStore.roomOccupancy"
      :disabled-fields="filterConfigurationStore.disabledParameterFields"
      :auto-detect-initial-values="filtersStore.hasUserInteracted"
      :loading="loading"
      @countries-change="handleCountriesChange"
      @room-occupancy-change="handleRoomOccupancyChange"
      @search-click="handleSearchClick"
    />
  </div>
  <div v-if="showFilters" class="lg:mb-10 mb-7 wrapper">
    <FilterBar
      v-model:selected-sort="selectedSort"
      v-model:fixed-bookable-only="fixedBookableOnly"
      :active-filters="activeFilters"
      :result-count="totalProducts"
      :sort-options="sortingOptions"
      @open-filters="handleOpenFilters"
      @remove-filter="handleRemoveFilter"
      @reset-filters="handleResetFilters"
      @sort-change="handleSortChange"
    />
  </div>

  <!-- Error state -->
  <div v-if="error" class="wrapper mb-6">
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
      {{ $t('ibe.product-list.error-loading', 'Error loading products:') }}
      {{
        typeof error === 'string' ? error : $t('ibe.product-list.error-unknown', 'Unknown error')
      }}
    </div>
  </div>

  <!-- Loading state with skeleton -->
  <div v-if="loading" class="wrapper mb-6">
    <div class="grid gap-8 md:gap-10 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      <ProductTeaserSkeleton v-for="i in filtersStore.itemsPerPage" :key="`skeleton-${i}`" />
    </div>
  </div>

  <!-- Empty state -->
  <div v-else-if="hasNoResults" class="wrapper mb-6">
    <ProductListEmptyState @reset-filters="handleResetToDefaults" />
  </div>

  <!-- Products -->
  <template v-else>
    <div class="wrapper">
      <ProductTeaserContainer
        :products="productsBeforeTeaser"
        :columns="3"
        :search-params="searchParams"
      />
    </div>
    <template v-if="showTeaser">
      <ResultListTeaser class="my-8 md:my-10" />
      <div class="wrapper">
        <ProductTeaserContainer
          :products="productsAfterTeaser"
          :columns="3"
          :search-params="searchParams"
        />
      </div>
    </template>
  </template>

  <div class="wrapper mt-10 lg:mt-20 mb-14">
    <BasePagination
      v-model:current-page="currentPageComputed"
      :total-items="totalProducts"
      :items-per-page="filtersStore.itemsPerPage"
    />
  </div>

  <DateRangeModal
    :date-range="filtersStore.dateRange"
    :duration="filtersStore.duration"
    :duration-mode="filtersStore.durationMode"
    :min-date="dateConstraints.min"
    :max-date="dateConstraints.max"
    @update:date-range="filtersStore.setDateRange"
    @update:duration="filtersStore.setDuration"
    @update:duration-mode="filtersStore.setDurationMode"
  />
  <ProductListModals />
</template>
